import { z } from 'zod'
import { type ProposalActivityKind } from '../enums/proposal-activity-kind.enum.js'
import { proposalChapterSchema } from '../enums/proposal-chapter.enum.js'
import { proposalPlanSchema } from '../enums/proposal-plan.enum.js'
import { MONEY_AMOUNT_PATTERN } from '../helpers/money.js'

/**
 * Text the page composed: a tree number, a sentence, the subject of a video.
 *
 * Trimmed and capped at the length the page cuts to before it sends, so a
 * stored value and a new batch are held to the same words.
 */
export const activityTextDetailSchema = z.string().trim().min(1).max(160)

/**
 * A budget typed into "Fit to a budget", in dollars.
 *
 * The same pattern as every other money amount on the wire. A sentence in
 * this field is not a budget, and slicing it would invent a number.
 */
export const activityMoneyDetailSchema = z
  .string()
  .trim()
  .regex(MONEY_AMOUNT_PATTERN, 'Enter a budget in dollars, e.g. 9000')

const occurredAtSchema = z.string().datetime()

const boundedSecondsSchema = z.number().int().min(0).max(3600).nullish()

/**
 * One thing a client did.
 *
 * Each kind carries only the fields that kind means. A READ names a chapter,
 * a PLAN names a plan, a BUDGET is money, a print is the pdf, and an open,
 * a share, or a presentation is the fact of it. A detail that does not
 * belong on the kind is refused here, instead of being stored and papered
 * over when somebody reads it back.
 */
export const activityEventSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('OPENED'),
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('READ'),
      detail: proposalChapterSchema,
      seconds: z.number().int().min(0).max(3600),
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('TREE'),
      detail: activityTextDetailSchema,
      seconds: boundedSecondsSchema,
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('PLAN'),
      detail: proposalPlanSchema,
      seconds: boundedSecondsSchema,
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('BUDGET'),
      detail: activityMoneyDetailSchema,
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('ADJUSTED'),
      detail: activityTextDetailSchema,
      seconds: boundedSecondsSchema,
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('VIDEO'),
      detail: activityTextDetailSchema,
      seconds: boundedSecondsSchema,
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('PRINTED'),
      detail: z.literal('pdf').nullish(),
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('SHARED'),
      occurredAt: occurredAtSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('PRESENTED'),
      occurredAt: occurredAtSchema,
    })
    .strict(),
])

type ActivityEventKind = z.infer<typeof activityEventSchema>['kind']

type MissingActivityKind = Exclude<ProposalActivityKind, ActivityEventKind>

type ExtraActivityKind = Exclude<ActivityEventKind, ProposalActivityKind>

/**
 * Fails the build when a kind is added to the enum and left out of a schema,
 * or added to a schema and left out of the enum.
 */
function assertActivityKindsCovered(
  covered: [MissingActivityKind, ExtraActivityKind] extends [never, never]
    ? true
    : never,
): true {
  return covered
}

assertActivityKindsCovered(true)

/**
 * A batch of what a client did, sent from the shared page.
 *
 * Unauthenticated like the rest of the shared routes, so it is bounded on
 * every side: a few dozen events a batch, short text, and a time the server
 * clamps to its own clock. It is a signal for a salesperson, so a malformed
 * batch is refused whole rather than half-stored.
 */
export const recordActivityInputSchema = z
  .object({
    events: z.array(activityEventSchema).min(1).max(50),
  })
  .strict()

export type RecordActivityInput = z.infer<typeof recordActivityInputSchema>

export type ActivityEvent = RecordActivityInput['events'][number]
